// Especialidade
import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { especialidadeService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  formatarPorcentagem,
  formatCurrencyPorcentagem,
} from "../../utils/FormatarValores";

const Especialidade = () => {
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });
  const [formData, setFormData] = useState({
    nome: "",
    valorPercentual: 0,
    valorPercentualFormatted: "0,0",
  });
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  useEffect(() => {
    loadEspecialidades();
  }, []);

  const loadEspecialidades = async (nome = "") => {
    setLoading(true);
    try {
      const data = await especialidadeService.listarEspecialidades(nome);
      setEspecialidades(data);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadEspecialidades(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  // Calcular dados da paginação
  const totalRegistros = especialidades.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const especialidadesPaginadas = especialidades.slice(
    indiceInicial,
    indiceFinal
  );

  // Funções de navegação da paginação
  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  // Gerar números das páginas para exibição
  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    // Ajustar início se estivermos no final
    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePercentualChange = (e) => {
    const { formatted, numeric } = formatarPorcentagem(e.target.value);
    setFormData((prev) => ({
      ...prev,
      valorPercentual: numeric,
      valorPercentualFormatted: formatted,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        nome: formData.nome,
        valorPercentual: formData.valorPercentual || 0,
      };

      if (editingItem) {
        await especialidadeService.atualizarEspecialidade(
          editingItem.id,
          dataToSend
        );
        showSuccess("Especialidade atualizada com sucesso!");
      } else {
        await especialidadeService.cadastrarEspecialidade(dataToSend);
        showSuccess("Especialidade criada com sucesso!");
      }

      await loadEspecialidades(searchTerm);
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar especialidade");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const { formatted } = formatarPorcentagem(
      (item.valorPercentual * 10).toString()
    );
    setFormData({
      nome: item.nome,
      valorPercentual: item.valorPercentual || 0,
      valorPercentualFormatted: formatted,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta especialidade?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await especialidadeService.deletarEspecialidade(id);
      showSuccess("Especialidade excluída com sucesso!");
      await loadEspecialidades(searchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir especialidade");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({
      nome: "",
      valorPercentual: 0,
      valorPercentualFormatted: "0,0",
    });
  };

  const handleNew = () => {
    setEditingItem(null);
    setFormData({
      nome: "",
      valorPercentual: 0,
      valorPercentualFormatted: "0,0",
    });
    setShowForm(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Especialidades
        </h1>
        <p className="text-gray-600">
          Gerencie as especialidades médicas disponíveis
        </p>
      </div>

      {/* Barra de Pesquisa e Botão Novo */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar por nome..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleNew}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Nova Especialidade
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? "Editar Especialidade" : "Nova Especialidade"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome da especialidade"
                maxLength={100}
              />
            </div>

            <div>
              <label
                htmlFor="valorPercentual"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Valor Percentual (%)
              </label>
              <input
                type="text"
                id="valorPercentual"
                name="valorPercentual"
                value={formData.valorPercentualFormatted}
                onChange={handlePercentualChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0,0"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSubmit}
              disabled={
                loading || !formData.nome.trim()
              }
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save size={16} />
              {loading ? "Salvando..." : "Salvar"}
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Lista de Especialidades</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && especialidades.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando especialidades...
          </div>
        ) : especialidades.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm
              ? "Nenhuma especialidade encontrada"
              : "Nenhuma especialidade cadastrada"}
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Percentual
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {especialidadesPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.nome}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrencyPorcentagem(item.valorPercentual ?? 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Primeira página */}
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    {/* Página anterior */}
                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Números das páginas */}
                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    {/* Próxima página */}
                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    {/* Última página */}
                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default Especialidade;
